import { useEffect, RefObject } from "react";

// 요소가 보이는지 확인하는 함수
const isElementVisible = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  const isVisible =
    rect.width > 0 && rect.height > 0 && rect.top >= 0 && rect.left >= 0;
  const style = window.getComputedStyle(element);
  const isReadonly = element.hasAttribute("readonly");

  return (
    isVisible &&
    style.visibility !== "hidden" &&
    style.display !== "none" &&
    style.opacity !== "0" &&
    !isReadonly
  );
};

const useFocusTrapping = (
  isOpen: boolean,
  modalRef: RefObject<HTMLDivElement>
) => {
  useEffect(() => {
    const ref = modalRef.current;

    if (!isOpen || !ref) return;

    const getVisibleFocusableElements = () => {
      // 오직 data-focus="true" 속성이 있는 요소들만 선택
      const focusableElements = Array.from(
        ref!.querySelectorAll('[data-focus="true"]')
      ) as HTMLElement[];

      // 화면에 보이는 요소만 필터링하고, 기본 포커스 가능한 요소(button 등)를 제외
      return focusableElements.filter(isElementVisible);
    };

    // 모달이 열리면 첫 번째 보이는 요소에 포커스 설정
    const visibleElements = getVisibleFocusableElements();

    if (visibleElements.length === 0) return;

    const firstElement = visibleElements[0];
    const lastElement = visibleElements[visibleElements.length - 1];

    firstElement.focus();

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      // 포커스 이동 시 visibleElements를 다시 가져와서 사용
      if (e.shiftKey) {
        // Shift + Tab: 첫 번째 요소에서 마지막 요소로 포커스 이동
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: 마지막 요소에서 첫 번째 요소로 포커스 이동
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    const trapFocus = (e: FocusEvent) => {
      if (ref && !ref.contains(e.target as Node)) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    ref.addEventListener("keydown", handleTabKey);
    ref.addEventListener("focusin", trapFocus); // 포커스가 모달 밖으로 나가는 것을 방지

    return () => {
      ref?.removeEventListener("keydown", handleTabKey);
      ref?.removeEventListener("focusin", trapFocus);
    };
  }, [isOpen, modalRef]);
};

export default useFocusTrapping;
